import * as React from 'react';

/**
 * Tailwind CSS breakpoints
 * @see https://tailwindcss.com/docs/responsive-design
 */
export const BREAKPOINTS = {
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  '2xl': 1536,
  '3xl': 1920,
  '4xl': 2560,
} as const;

export type Breakpoint = keyof typeof BREAKPOINTS;

/**
 * A hook that detects the current screen breakpoint based on window width.
 * Uses Tailwind CSS breakpoints for consistency with CSS classes.
 *
 * @returns {Breakpoint | 'base'} The current breakpoint name ('base', 'sm', 'md', 'lg', 'xl', '2xl', '3xl', '4xl')
 *
 * @example
 * ```tsx
 * function ResponsiveComponent() {
 *   const breakpoint = useBreakpoint();
 *
 *   if (breakpoint === 'base' || breakpoint === 'sm') {
 *     return <MobileView />;
 *   }
 *   return <DesktopView />;
 * }
 * ```
 */
export function useBreakpoint(): Breakpoint | 'base' {
  const [breakpoint, setBreakpoint] = React.useState<Breakpoint | 'base'>(() => {
    if (typeof window === 'undefined') {
      return 'base';
    }
    return getBreakpoint(window.innerWidth);
  });

  React.useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const handleResize = () => {
      const newBreakpoint = getBreakpoint(window.innerWidth);
      setBreakpoint(newBreakpoint);
    };

    handleResize();

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return breakpoint;
}

/**
 * Helper function to determine the current breakpoint based on window width
 */
function getBreakpoint(width: number): Breakpoint | 'base' {
  if (width >= BREAKPOINTS['4xl']) return '4xl';
  if (width >= BREAKPOINTS['3xl']) return '3xl';
  if (width >= BREAKPOINTS['2xl']) return '2xl';
  if (width >= BREAKPOINTS.xl) return 'xl';
  if (width >= BREAKPOINTS.lg) return 'lg';
  if (width >= BREAKPOINTS.md) return 'md';
  if (width >= BREAKPOINTS.sm) return 'sm';
  return 'base';
}
